import { useI18n } from '@nx/i18n';
import { isRegistrationOpen } from '@nx/domain';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getRegistrationLanding, submitRegistration } from '../../services/eoCallables';
import type { RegistrationLandingData } from '../../services/eoCallables';
import { useAppSelector } from '../../store/hooks';
import '@nx/ui/eo';

export function RegistrationLandingPage() {
  const { t } = useI18n();
  const { eventId = '' } = useParams();
  const navigate = useNavigate();
  const identity = useAppSelector((s) => s.auth.identity);
  const [event, setEvent] = useState<RegistrationLandingData | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const data = await getRegistrationLanding(eventId);
        setEvent(data);
      } catch {
        setEvent(null);
      }
    })();
  }, [eventId]);

  const openState =
    event &&
    isRegistrationOpen({
      registrationDeadline: event.registrationDeadline
        ? new Date(event.registrationDeadline)
        : null,
      maxParticipants: event.maxParticipants,
      currentCount: event.registrationCount,
    });

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!identity) {
      navigate(`/login?next=${encodeURIComponent(`/register/${eventId}`)}`);
      return;
    }
    try {
      await submitRegistration(eventId, { name, email, company });
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('eo.register.failed'));
    }
  }

  if (!event) {
    return (
      <div className="eo-main" style={{ maxWidth: 720, margin: '40px auto' }}>
        <p>{t('common.loading')}</p>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="landing-shell" style={{ margin: '40px auto' }}>
        <h2>{t('eo.register.successTitle')}</h2>
        <p>{t('eo.register.successBody')}</p>
      </div>
    );
  }

  const startLabel = event.startDate?.slice(0, 10) ?? '—';
  const endLabel = event.endDate?.slice(0, 10) ?? '—';

  return (
    <div className="eo-shell" style={{ gridTemplateColumns: '1fr' }}>
      <main className="eo-main">
        <div className="landing-shell">
          <div className="landing-cover">
            <span className="badge-when">
              {startLabel} — {endLabel}
            </span>
          </div>
          <h2>{event.title}</h2>
          <p className="desc">{event.description}</p>
          <p className="muted-text">{event.location}</p>

          {!openState?.open ? (
            <div className="landing-form">
              <p>{openState?.reason === 'full' ? t('eo.register.full') : t('eo.register.closed')}</p>
            </div>
          ) : (
            <form className="landing-form" onSubmit={handleSubmit}>
              {!identity ? (
                <p>
                  <Link to={`/login?next=${encodeURIComponent(`/register/${eventId}`)}`}>
                    {t('eo.register.signInFirst')}
                  </Link>
                </p>
              ) : null}
              <div className="field">
                <label>{t('eo.participants.name')}</label>
                <input className="input" value={name} onChange={(e) => setName(e.target.value)} required />
              </div>
              <div className="field">
                <label>{t('eo.participants.email')}</label>
                <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
              </div>
              <div className="field">
                <label>{t('eo.participants.company')}</label>
                <input className="input" value={company} onChange={(e) => setCompany(e.target.value)} />
              </div>
              {error ? <p className="err">{error}</p> : null}
              <button type="submit" className="btn btn-primary btn-lg" style={{ width: '100%' }}>
                {t('eo.register.submit')}
              </button>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}
